import type { TFunction } from "i18next";
import { AtLg } from "@/components/breakpoint";
import type { SprintMission } from "./types";
import { missionRewardPrize } from "../missions/types";
import { formatButtonPrizeTitle } from "@/utils/helpers";
import { sprintMissionPhase, URGENT_TIME_MS } from "./types";
import { timeLeftRing } from "@/igw-button/ring";

import { registerCampaignType } from "../../registry";
import { canJoinCampaign } from "../../can-join";
import type { CampaignTypeModule, CurrentCampaign } from "../../registry";
import type { AppStore } from "@/stores/app.store";
import { selectSprintMissionById } from "@/stores/sprint-mission.store";
import { selectJoined } from "@/stores/campaigns.store";
import {
  isActiveCampaignStatus,
  isFinishedCampaignStatus,
} from "../../campaign-status";

import type { CampaignView } from "@/components/campaigns/campaign-shell/campaign-view";
import { cn } from "@/lib/utils";
import { FINISHED_DIM } from "@/components/campaigns/campaign-shell/campaign-view";
import {
  CampaignDescriptionText,
  EligibleGamesSection,
  HowItWorksCard,
  InfoSection,
  CampaignTimeLeftChip,
} from "@/components/campaigns/campaign-shell/campaign-sections";
import {
  CampaignJoinCta,
  CampaignJoinStickyBar,
} from "@/components/campaign-card/campaign-join-cta";
import { SprintMissionMissionCard } from "@/components/campaigns/sprint-mission/sprint-mission-mission-card";
import { SprintMissionPayoutStrip } from "@/components/campaigns/sprint-mission/sprint-mission-payout-strip";
import { SprintMissionStatusCard } from "@/components/campaigns/sprint-mission/sprint-mission-status-card";
import { SprintMissionWinnersCard } from "@/components/campaigns/sprint-mission/sprint-mission-winners-card";
import { SprintMissionDockPanel } from "@/components/campaigns/sprint-mission/sprint-mission-dock-panel";
import { SprintMissionHomeCard } from "@/components/campaigns/sprint-mission/sprint-mission-home-card";
import { SprintMissionLatestWinsCard } from "@/components/campaigns/sprint-mission/sprint-mission-latest-wins-card";
import { FinishedSprintMissionCard } from "@/components/campaign-card/finished-campaign/finished-sprint-mission-card";

const sprintMissionData = (current: CurrentCampaign): SprintMission =>
  current.data as SprintMission;

function detailView(
  current: CurrentCampaign,
  t: TFunction
): CampaignView | null {
  if (current.type !== "sprint-mission") return null;
  const lm = sprintMissionData(current);
  // Finished = ended by date OR pool exhausted (the status flip can arrive one push later).
  const finished = sprintMissionPhase(lm) === "finished";
  const joined = current.joined;
  // A drained pool is over for joining too, even while the status flip is a push away.
  const canJoin = !finished && canJoinCampaign(lm.campaign, joined);

  // "N winners" is the pool size; before the pool stream has pushed, say it without the number.
  const winners = lm.pool?.prize_pool_size;
  const howItWorks = (
    <HowItWorksCard
      text={
        winners
          ? t(
              "sprintMission.howItWorksWinners",
              "Complete the mission to be one of {{count}} winners. When the last winner is in, it's over.",
              { count: winners }
            )
          : t(
              "sprintMission.howItWorksWinnersNoCount",
              "Complete the mission to be one of a limited number of winners. When the last winner is in, it's over."
            )
      }
      minimumBet={lm.minimum_bet}
    />
  );

  return {
    campaign: lm.campaign,
    primaryLabel: t("nav.overview", "Overview"),
    finished,
    // No `mobileNotice`: the status card lives in `primary`, so it renders in the same spot on
    // both layouts. The generic `CampaignFinishedCard` is deliberately NOT used for this type —
    // it prints `campaign.end`, which is wrong for a pool that ran dry before its date.
    // "Latest wins" sits under "How it works" on both layouts; it renders nothing until the
    // first claim arrives on the pool stream.
    sidebar: finished ? (
      <div className={cn(FINISHED_DIM, "flex w-full flex-col gap-2 lg:gap-4")}>
        <AtLg>{howItWorks}</AtLg>
        <SprintMissionLatestWinsCard sprintMission={lm} />
      </div>
    ) : (
      <>
        {canJoin && (
          <CampaignJoinCta campaign={lm.campaign} className="hidden lg:flex" />
        )}
        <AtLg>{howItWorks}</AtLg>
        <SprintMissionLatestWinsCard sprintMission={lm} />
      </>
    ),
    guide: finished ? (
      <div className={FINISHED_DIM}>{howItWorks}</div>
    ) : (
      howItWorks
    ),
    // Status card (won / finished) on top, then the winners card, then the mission card — there
    // is no ladder here (one mission, one prize). Each card owns its own finished dimming, so the
    // winners card can stay readable while the mission card fades.
    primary: (
      <div className="flex w-full flex-col gap-2 lg:gap-4">
        <SprintMissionStatusCard sprintMission={lm} />
        {/* Directly beneath the won card (design); renders nothing unless the pool pays out
            at campaign end. */}
        <SprintMissionPayoutStrip sprintMission={lm} />
        <SprintMissionWinnersCard sprintMission={lm} />
        <SprintMissionMissionCard sprintMission={lm} />
      </div>
    ),
    // Sideways: the shared pool — winners left and who just won — in the narrow column on the
    // left with the Opt In, the player's mission (and its won/finished status) on the right.
    landscape: {
      narrowSide: "start",
      wide: (
        <>
          <SprintMissionStatusCard sprintMission={lm} />
          <SprintMissionPayoutStrip sprintMission={lm} />
          <SprintMissionMissionCard sprintMission={lm} />
        </>
      ),
      narrow: (
        <>
          <SprintMissionWinnersCard sprintMission={lm} />
          <SprintMissionLatestWinsCard sprintMission={lm} />
        </>
      ),
    },
    info: (
      <div className="flex w-full flex-col gap-2 lg:gap-4">
        <InfoSection
          title={t("campaign.aboutHeading", "About")}
          aside={<CampaignTimeLeftChip campaign={lm.campaign} />}
        >
          <CampaignDescriptionText text={lm.campaign.campaign_info} />
        </InfoSection>
        <EligibleGamesSection
          games={lm.eligible_games}
          count={lm.eligible_games_count}
          campaignId={lm.campaign.id}
        />
      </div>
    ),
    canJoin,
    mobileStickyAction: canJoin ? (
      <CampaignJoinStickyBar campaign={lm.campaign} />
    ) : undefined,
  };
}

/** Sprint Mission (`campaign.type === "MI"` AND `campaign.subtype === "SM"`). */
const sprintMissionModule: CampaignTypeModule = {
  id: "sprint-mission",
  // The coin drains the time to the end, urgent in the same window the
  // Sprint's own "closing soon" uses.
  launcher: {
    ring: (campaign, now) => timeLeftRing(campaign, now, URGENT_TIME_MS),
  },
  resolve(state: AppStore, key): CurrentCampaign | null {
    const lm = selectSprintMissionById(state, key);
    if (!lm) return null;
    return {
      type: "sprint-mission",
      campaign: lm.campaign,
      joined: selectJoined(state, key),
      data: lm,
    };
  },
  listActive: (state) =>
    state.sprintMissions
      .filter((lm) => isActiveCampaignStatus(lm.campaign.status))
      .map((lm) => ({ campaign: lm.campaign, data: lm })),
  listFinished: (state) =>
    state.sprintMissions
      .filter((lm) => isFinishedCampaignStatus(lm.campaign.status))
      .map((lm) => ({ campaign: lm.campaign, data: lm })),
  hook: (entry, t) => {
    const sm = entry.data as SprintMission;
    const prize = sm.mission ? missionRewardPrize(sm.mission) : null;
    return prize
      ? {
          label: t("sprintMission.oneTimePrize", "One time prize"),
          value: formatButtonPrizeTitle(prize.title, t),
          isCatalogItem: prize.is_catalog_item,
        }
      : null;
  },
  homeCard: (entry, layout) => (
    <SprintMissionHomeCard
      sprintMission={entry.data as SprintMission}
      layout={layout}
    />
  ),
  finishedCard: (entry) => (
    <FinishedSprintMissionCard sprintMission={entry.data as SprintMission} />
  ),
  detailView,
  dockPanel: (current) =>
    current.type === "sprint-mission" ? (
      <SprintMissionDockPanel
        sprintMission={sprintMissionData(current)}
        joined={current.joined}
      />
    ) : null,
};

registerCampaignType(sprintMissionModule);
